import React, { useEffect } from 'react';

/**
 * Full-page paper overlay used by both sport screens (creation + session).
 *
 * Mobile first: it fills the viewport with `100dvh` so the iOS URL bar cannot
 * hide the footer, and only the body scrolls — the title row with the ✕ stays
 * put.
 *
 * @param {string} title shown top-left
 * @param {string} [subtitle] small line under the title
 * @param {() => void} onClose fired by ✕, Escape and the backdrop
 * @param {React.ReactNode} [footer] sticky action row at the bottom
 * @param {React.ReactNode} children scrollable body
 */
export default function FullScreenSheet({ title, subtitle, onClose, footer, children }) {
  // Escape closes, and the page behind must not scroll while we are open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // paper-dots repaints the page's own dotted grid, which the opaque
      // background would otherwise hide.
      className="paper-dots fixed inset-0 z-50 flex flex-col"
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-dashed border-line px-4 py-3 sm:px-6">
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-base uppercase tracking-tight">{title}</h2>
          {subtitle && <p className="truncate font-hand text-lg text-ink-soft">{subtitle}</p>}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-mr-1 shrink-0 rounded-pill px-2 py-1 text-lg leading-none text-ink-soft
                     transition-colors hover:bg-accent-soft hover:text-accent"
        >
          ✕
        </button>
      </header>

      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <div className="mx-auto w-full max-w-2xl">{children}</div>
      </div>

      {footer && (
        <footer className="paper-dots shrink-0 border-t border-dashed border-line px-4 py-3 sm:px-6">
          <div className="mx-auto w-full max-w-2xl">{footer}</div>
        </footer>
      )}
    </div>
  );
}
